import { useState } from "react";
import { api } from "../lib/api";
import { CONTACT, SERVICES } from "../lib/constants";
import { SUBURBS_SEO } from "../lib/suburbs";
import { ArrowRight, ImageSquare, CheckCircle, X } from "@phosphor-icons/react";

const PROPERTY_TYPES = ["House", "Apartment", "Townhouse", "Office", "Retail", "Hospitality", "Other"];
const SUBURBS = SUBURBS_SEO.map((s) => s.name).sort();

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    suburb: "",
    service: "",
    property_type: "",
    message: "",
  });
  const [imageBase64, setImageBase64] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImageBase64(reader.result);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      setError("Please fill in name, email, phone, and service.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await api.post("/leads", { ...form, image_base64: imageBase64 || null });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit, try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="book" data-testid="booking-section" className="relative py-20 md:py-32 px-5 md:px-10 lg:px-16">
        <div className="max-w-3xl mx-auto border-2 border-[#111] p-8 md:p-14 bg-[#F2F2EF] shadow-brut text-center">
          <CheckCircle size={64} weight="duotone" className="text-[#6A7B61] mx-auto" />
          <h3 className="font-display text-4xl md:text-6xl uppercase tracking-tighter mt-4">Thanks, {form.name.split(' ')[0] || 'mate'}!</h3>
          <p className="mt-4 text-lg">
            We've got your details. Gurdeep will give you a buzz on <strong>{form.phone}</strong> within 24 hours
            to lock in your free on-site quote.
          </p>
          <p className="mt-2 text-sm text-[#666]">In a hurry? Call us straight on {CONTACT.phoneDisplay}.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="book" data-testid="booking-section" className="relative py-20 md:py-32 px-5 md:px-10 lg:px-16">
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 lg:col-span-5">
          <p className="section-num mb-3">08 — Book a quote</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9]">
            Free on-site<br />quote.<br />
            <span className="text-[#D85C3A]">In 24 hours.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed max-w-md">
            Send us a few details and snap a photo of the space. Gurdeep will reach out personally
            within a day to book a no-pressure walkthrough.
          </p>
          <ul className="mt-8 space-y-3 text-base">
            {[
              "Fixed-price quote — no surprise upcharges",
              "Insured and Dulux accredited",
              "Weekend availability",
              "Heritage and rental compliance",
            ].map((b) => (
              <li key={b} className="flex items-center gap-3">
                <CheckCircle size={20} weight="duotone" className="text-[#D85C3A]" /> {b}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} data-testid="booking-form" className="col-span-12 lg:col-span-7 border-2 border-[#111] bg-[#F2F2EF] p-6 md:p-10 shadow-brut">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#666]">Full name *</label>
              <input data-testid="form-name" className="input-brut mt-1" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Sarah Albert" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#666]">Phone *</label>
              <input data-testid="form-phone" className="input-brut mt-1" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="0412 345 678" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[#666]">Email *</label>
              <input data-testid="form-email" type="email" className="input-brut mt-1" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#666]">Suburb</label>
              <select data-testid="form-suburb" className="input-brut mt-1" value={form.suburb} onChange={(e) => update("suburb", e.target.value)}>
                <option value="">Select your suburb</option>
                {SUBURBS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#666]">Property type</label>
              <select data-testid="form-property" className="input-brut mt-1" value={form.property_type} onChange={(e) => update("property_type", e.target.value)}>
                <option value="">Select</option>
                {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[#666]">Service needed *</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SERVICES.map((s) => (
                  <button
                    type="button"
                    key={s.key}
                    data-testid={`form-service-${s.key}`}
                    onClick={() => update("service", s.key)}
                    className={`chip ${form.service === s.key ? "active" : ""}`}
                  >
                    {s.title}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[#666]">Tell us about the job</label>
              <textarea data-testid="form-message" rows={3} className="input-brut mt-1" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="3 bedrooms + living, walls and ceilings. Some patching needed." />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-[#666]">Photo of space (optional)</label>
              {imagePreview ? (
                <div className="mt-2 relative inline-block border-2 border-[#111]">
                  <img src={imagePreview} alt="upload preview" className="max-h-48" />
                  <button
                    type="button"
                    onClick={() => { setImageBase64(""); setImagePreview(""); }}
                    className="absolute -top-3 -right-3 bg-[#D85C3A] border-2 border-[#111] p-1.5 text-white"
                    aria-label="remove"
                    data-testid="form-remove-image"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ) : (
                <label data-testid="form-upload" className="mt-2 border-2 border-dashed border-[#111] p-6 flex flex-col items-center gap-2 cursor-pointer hover:bg-[#EAEAE5] transition-colors">
                  <ImageSquare size={28} weight="duotone" />
                  <span className="text-sm">Click to upload (max 5MB)</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                </label>
              )}
            </div>
          </div>

          {error && <div className="mt-4 text-sm text-[#B94A2E] font-bold">{error}</div>}

          <button type="submit" data-testid="form-submit" disabled={loading} className="btn-brut mt-8 disabled:opacity-60">
            {loading ? "Sending..." : "Request my free quote"} <ArrowRight size={18} weight="bold" />
          </button>
          <p className="mt-3 text-xs text-[#666]">By submitting, you agree to be contacted by The Melbourne Painters.</p>
        </form>
      </div>
    </section>
  );
}
